import {Injectable} from '@angular/core';

interface ColorMap {
  [key: string]: { r: number; g: number; b: number; };
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private colorNameToRgb: ColorMap = {
    white: {r: 255, g: 255, b: 255},
    black: {r: 0, g: 0, b: 0},
    red: {r: 255, g: 0, b: 0},
    lime: {r: 0, g: 255, b: 0},
    blue: {r: 0, g: 0, b: 255},
    yellow: {r: 255, g: 255, b: 0},
    cyan: {r: 0, g: 255, b: 255},
    magenta: {r: 255, g: 0, b: 255},
    silver: {r: 192, g: 192, b: 192},
    gray: {r: 128, g: 128, b: 128},
    maroon: {r: 128, g: 0, b: 0},
    olive: {r: 128, g: 128, b: 0},
    green: {r: 0, g: 128, b: 0},
    purple: {r: 128, g: 0, b: 128},
    teal: {r: 0, g: 128, b: 128},
    navy: {r: 0, g: 0, b: 128}
    // Add other colors as needed
  };

  constructor() {
  }

  initializeTheme() {
    this.setHSLVariables('--primary-color');
    this.setHSLVariables('--primary-contrast-color');
    this.setHSLVariables('--secondary-color');
  }

  private setHSLVariables(variableName: string) {
    const color = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    const hsl = this.convertToHsl(color);
    const baseName = variableName;

    document.documentElement.style.setProperty(`${baseName}-h`, `${hsl.h}deg`);
    document.documentElement.style.setProperty(`${baseName}-s`, `${hsl.s * 100}%`);
    document.documentElement.style.setProperty(`${baseName}-l`, `${hsl.l * 100}%`);
  }

  private convertToHsl(color: string) {
    let r, g, b;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      [r, g, b] = color.match(/\d+/g)?.map(Number);
    } else if (this.colorNameToRgb[color.toLowerCase()]) {
      ({r, g, b} = this.colorNameToRgb[color.toLowerCase()]);
    } else {
      console.error(`Color ${color} is not recognized`);
      return {h: 0, s: 0, l: 0}; // Default to an arbitrary value or handle error
    }
    return this.rgbToHsl(r, g, b);
  }

  private rgbToHsl(r: number, g: number, b: number) {
    r /= 255, g /= 255, b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
          break;
      }
      h /= 6;
      h *= 360;
    }

    return {h, s, l};
  }
}
